export default function SearchableSelect() {
  function initSelect(container) {
    const input = container.querySelector(".c-select__input");
    const listbox = container.querySelector(".c-select__listbox");
    const nativeSelect = container.querySelector('select[aria-hidden="true"]');
    const noResults = container.querySelector(".c-select__no-results");
    const tagsContainer = container.querySelector(".c-select__tags");
    const clearButton = container.querySelector(".c-select__clear");
    const announcer = container.querySelector(".c-select__announcer");

    if (!input || !listbox || !nativeSelect) return;

    const isMulti = container.hasAttribute("data-multi");
    let activeIndex = -1;
    const allOptions = Array.from(
      listbox.querySelectorAll(".c-select__option:not(.c-select__no-results)"),
    );

    function getVisibleOptions() {
      return allOptions.filter(
        (opt) => !opt.hidden && !opt.classList.contains("is-disabled"),
      );
    }

    function open() {
      listbox.hidden = false;
      input.setAttribute("aria-expanded", "true");
      container.classList.add("is-open");
      filterOptions(input.value);
    }

    function close() {
      listbox.hidden = true;
      input.setAttribute("aria-expanded", "false");
      container.classList.remove("is-open");
      input.removeAttribute("aria-activedescendant");
      setHighlight(-1);
      activeIndex = -1;
    }

    function isOpen() {
      return !listbox.hidden;
    }

    function setHighlight(index) {
      const visible = getVisibleOptions();
      allOptions.forEach((opt) => opt.classList.remove("is-highlighted"));
      if (index >= 0 && index < visible.length) {
        visible[index].classList.add("is-highlighted");
        input.setAttribute("aria-activedescendant", visible[index].id);
        visible[index].scrollIntoView({ block: "nearest" });
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    }

    function updateClearVisibility() {
      if (!clearButton) return;
      clearButton.hidden = isMulti
        ? !tagsContainer?.querySelector(".c-select__tag")
        : !input.dataset.value;
    }

    function clearAll() {
      allOptions.forEach((opt) => {
        opt.setAttribute("aria-selected", "false");
        opt.classList.remove("is-selected");
      });
      Array.from(nativeSelect.options).forEach((o) => (o.selected = false));

      if (isMulti && tagsContainer) {
        tagsContainer.innerHTML = "";
      } else {
        input.value = "";
        input.dataset.value = "";
        nativeSelect.value = "";
      }

      filterOptions("");
      updateClearVisibility();
      if (announcer) announcer.textContent = "Selection cleared";
      nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function addTag(value, label) {
      if (!tagsContainer) return;
      const tag = document.createElement("li");
      tag.className = "c-select__tag";
      tag.dataset.value = value;

      const span = document.createElement("span");
      span.textContent = label;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "c-select__tag-remove";
      btn.setAttribute("aria-label", `Remove ${label}`);
      btn.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="0.75em" height="0.75em"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>`;

      tag.appendChild(span);
      tag.appendChild(btn);
      tagsContainer.appendChild(tag);
    }

    function removeTag(value) {
      tagsContainer
        ?.querySelector(`[data-value="${CSS.escape(value)}"]`)
        ?.remove();
    }

    function deselect(value, label) {
      const opt = allOptions.find((o) => o.dataset.value === value);
      if (opt) {
        opt.setAttribute("aria-selected", "false");
        opt.classList.remove("is-selected");
      }
      const nativeOpt = nativeSelect.querySelector(
        `option[value="${CSS.escape(value)}"]`,
      );
      if (nativeOpt) nativeOpt.selected = false;
      removeTag(value);
      updateClearVisibility();
      if (announcer) announcer.textContent = `${label} removed`;
      nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function selectOption(opt) {
      const value = opt.dataset.value ?? "";
      const label = opt.textContent?.trim() ?? "";

      if (isMulti) {
        const alreadySelected = opt.classList.contains("is-selected");
        const nativeOpt = nativeSelect.querySelector(
          `option[value="${CSS.escape(value)}"]`,
        );

        opt.setAttribute("aria-selected", String(!alreadySelected));
        opt.classList.toggle("is-selected", !alreadySelected);
        if (nativeOpt) nativeOpt.selected = !alreadySelected;

        if (alreadySelected) {
          removeTag(value);
          if (announcer) announcer.textContent = `${label} removed`;
        } else {
          addTag(value, label);
          if (announcer) announcer.textContent = `${label} selected`;
        }

        input.value = "";
        filterOptions("");
        updateClearVisibility();
        input.focus();
        nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        input.value = label;
        input.dataset.value = value;
        nativeSelect.value = value;

        allOptions.forEach((o) => {
          const isSelected = o.dataset.value === value;
          o.setAttribute("aria-selected", String(isSelected));
          o.classList.toggle("is-selected", isSelected);
        });

        updateClearVisibility();
        close();
        input.focus();
        nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        if (announcer) announcer.textContent = `${label} selected`;
      }
    }

    function filterOptions(query) {
      const q = query.toLowerCase().trim();
      let visibleCount = 0;

      allOptions.forEach((opt) => {
        const label = opt.textContent?.toLowerCase().trim() ?? "";
        const match = q === "" || label.includes(q);
        opt.hidden = !match;
        if (match) visibleCount++;
      });

      if (noResults) {
        noResults.hidden = visibleCount > 0;
      }

      if (announcer && q !== "") {
        announcer.textContent =
          visibleCount === 0
            ? "No results found"
            : `${visibleCount} option${visibleCount === 1 ? "" : "s"} available`;
      }

      activeIndex = -1;
      setHighlight(-1);
    }

    input.addEventListener("input", () => {
      if (!isOpen()) open();
      filterOptions(input.value);
    });

    input.addEventListener("click", () => {
      if (isMulti) {
        if (!isOpen()) open();
      } else {
        if (isOpen()) {
          close();
        } else {
          open();
        }
      }
    });

    input.addEventListener("focus", () => {
      if (!isMulti) input.select();
    });

    input.addEventListener("keydown", (e) => {
      const visible = getVisibleOptions();

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen()) {
            open();
            activeIndex = -1;
          }
          activeIndex = Math.min(activeIndex + 1, visible.length - 1);
          setHighlight(activeIndex);
          break;

        case "ArrowUp":
          e.preventDefault();
          if (!isOpen()) {
            open();
            activeIndex = visible.length;
          }
          activeIndex = Math.max(activeIndex - 1, 0);
          setHighlight(activeIndex);
          break;

        case "Home":
          if (isOpen()) {
            e.preventDefault();
            activeIndex = 0;
            setHighlight(activeIndex);
          }
          break;

        case "End":
          if (isOpen()) {
            e.preventDefault();
            activeIndex = visible.length - 1;
            setHighlight(activeIndex);
          }
          break;

        case "Enter":
          e.preventDefault();
          if (isOpen() && activeIndex >= 0 && visible[activeIndex]) {
            selectOption(visible[activeIndex]);
          } else if (!isOpen()) {
            open();
          }
          break;

        case "Escape":
          if (isOpen()) {
            e.preventDefault();
            if (!isMulti) {
              const selected = allOptions.find((o) =>
                o.classList.contains("is-selected"),
              );
              input.value = selected?.textContent?.trim() ?? "";
            } else {
              input.value = "";
            }
            close();
          }
          break;

        case "Backspace":
          if (isMulti && input.value === "" && tagsContainer) {
            const lastTag = tagsContainer.querySelector(
              ".c-select__tag:last-child",
            );
            if (lastTag) {
              const v = lastTag.dataset.value ?? "";
              const l =
                lastTag.querySelector("span")?.textContent?.trim() ?? "";
              deselect(v, l);
            }
          }
          break;

        case "Tab":
          if (isOpen()) {
            if (!isMulti && activeIndex >= 0 && visible[activeIndex]) {
              selectOption(visible[activeIndex]);
            } else {
              close();
            }
          }
          break;
      }
    });

    clearButton?.addEventListener("click", (e) => {
      e.stopPropagation();
      clearAll();
      input.focus();
    });

    listbox.addEventListener("click", (e) => {
      const opt = e.target.closest(".c-select__option");
      if (
        opt &&
        !opt.classList.contains("is-disabled") &&
        !opt.classList.contains("c-select__no-results")
      ) {
        selectOption(opt);
      }
    });

    listbox.addEventListener("mouseover", (e) => {
      const opt = e.target.closest(".c-select__option");
      if (
        opt &&
        !opt.classList.contains("is-disabled") &&
        !opt.hidden &&
        !opt.classList.contains("c-select__no-results")
      ) {
        const visible = getVisibleOptions();
        activeIndex = visible.indexOf(opt);
        setHighlight(activeIndex);
      }
    });

    tagsContainer?.addEventListener("click", (e) => {
      const btn = e.target.closest(".c-select__tag-remove");
      if (btn) {
        e.stopPropagation();
        const tag = btn.closest(".c-select__tag");
        if (tag) {
          const v = tag.dataset.value ?? "";
          const l = tag.querySelector("span")?.textContent?.trim() ?? "";
          deselect(v, l);
          input.focus();
        }
      }
    });

    document.addEventListener("click", (e) => {
      if (!container.contains(e.target)) {
        if (isOpen()) {
          if (!isMulti) {
            const selected = allOptions.find((o) =>
              o.classList.contains("is-selected"),
            );
            input.value = selected?.textContent?.trim() ?? "";
          } else {
            input.value = "";
          }
          close();
        }
      }
    });
  }

  document.querySelectorAll("[data-select]").forEach(initSelect);
}
